import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import { LogOut, ChevronDown, Globe } from 'lucide-react';

const TopNavbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, logout: authLogout } = useAuthStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sportingMenuOpen, setSportingMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const sportingMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    console.log('Logout button clicked - executing logout...');
    
    // Cerrar el menú
    setUserMenuOpen(false);
    
    // Mostrar mensaje de logout
    toast.success(t('auth.logoutSuccess'));
    
    // Ejecutar logout directo del store
    authLogout();
    
    // Redirigir inmediatamente
    setTimeout(() => {
      window.location.href = '/login';
    }, 500);
  };
  
  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (sportingMenuRef.current && !sportingMenuRef.current.contains(event.target as Node)) {
        setSportingMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigation = [
    {
      name: t('nav.dashboard'),
      href: '/dashboard',
      current: location.pathname === '/dashboard',
      disabled: false
    },
    {
      name: t('nav.sporting'),
      href: '/sporting',
      current: location.pathname.startsWith('/sporting'),
      disabled: false,
      hasDropdown: true
    }
  ];

  return (
    <nav className="bg-gradient-sidebar-menu border-b border-blue-800/30 shadow-medium sticky top-0 z-50">
      <div className="px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center group">
            <div className="text-white rounded-xl p-3 group-hover:scale-105 transition-all duration-300">
              <img
                src="/images/logo_scoutmind_white.png"
                alt="ScoutMind AI Logo"
                className="h-12 w-auto"
                style={{ objectFit: "contain" }}
              />
            </div>
          </Link>

          {/* Navigation items */}
          <div className="flex items-center space-x-1">
            {navigation.map((item) => {

              if (item.hasDropdown && item.name === t('nav.sporting')) {
                return (
                  <div key={item.name} className="relative" ref={sportingMenuRef}>
                    <button
                      onClick={() => setSportingMenuOpen(!sportingMenuOpen)}
                      className={`group flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                        item.current
                          ? 'bg-white/20 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <div className={`p-1.5 rounded-md transition-all duration-300 ${
                        item.current 
                          ? 'bg-white/20' 
                          : 'group-hover:bg-white/10'
                      }`}>
                    
                      </div>
                      <span className="ml-2">{item.name}</span>
                      <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${
                        sportingMenuOpen ? 'rotate-180' : ''
                      }`} />
                    </button>

                    {/* Sporting dropdown */}
                    {sportingMenuOpen && (
                      <div className="absolute left-0 mt-2 w-64 bg-gradient-sidebar-menu border border-blue-800/30 rounded-lg shadow-large py-2">
                        <Link
                          to="/sporting/player-comparison"
                          className="flex items-center px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
                          onClick={() => setSportingMenuOpen(false)}
                        >
                          <span className="font-medium">{t('sporting.playerComparison')}</span>
                        </Link>
                        <Link
                          to="/sporting/expiring-contracts"
                          className="flex items-center px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
                          onClick={() => setSportingMenuOpen(false)}
                        >
                          <span className="font-medium">{t('sporting.marketOpportunities')}</span>
                        </Link>
                        <Link
                          to="/sporting/reportes-jugadores"
                          className="flex items-center px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
                          onClick={() => setSportingMenuOpen(false)}
                        >
                          <span className="font-medium">Reportes de Jugadores</span>
                        </Link>
                        <Link
                          to="/sporting/countries"
                          className="flex items-center px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
                          onClick={() => setSportingMenuOpen(false)}
                        >
                          <span className="font-medium">Explorador por Nacionalidad</span>
                        </Link>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.disabled ? '#' : item.href}
                  className={`group flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    item.current
                      ? 'bg-white/20 text-white shadow-lg'
                      : item.disabled
                      ? 'text-white/40 cursor-not-allowed opacity-60'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                  onClick={(e) => {
                    if (item.disabled) {
                      e.preventDefault();
                    }
                  }}
                >
                  <div className={`p-1.5 rounded-md transition-all duration-300 ${
                    item.current 
                      ? 'bg-white/20' 
                      : 'group-hover:bg-white/10'
                  }`}>
          
                  </div>
                  <span className="ml-2">{item.name}</span>
                  {item.disabled && (
                    <span className="ml-2 text-xs bg-white/20 text-white/80 px-2 py-1 rounded-full border border-white/30">
                      {t('common.comingSoon')}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-3">
            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 hover:border-white/30 transition-all duration-300"
            >
              <Globe className="w-4 h-4" />
              <span>{i18n.language.toUpperCase()}</span>
            </button>

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-3 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg border border-transparent hover:border-white/20 transition-all duration-300"
              >
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center shadow-lg">
                  <span className="text-white font-semibold text-sm">
                    {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                  </span>
                </div>
                <div className="hidden lg:block text-left">
                  <p className="text-white font-semibold text-sm truncate max-w-[150px]">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-white/70 text-xs font-medium truncate max-w-[150px]">
                    {user?.club?.name}
                  </p>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* User dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-gradient-sidebar-menu border border-blue-800/30 rounded-lg shadow-large py-2">
                  <div className="px-4 py-3 border-b border-blue-800/30">
                    <p className="text-sm font-semibold text-white">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-white/70 font-medium">
                      {user?.role?.name ? t(`roles.${user.role.name}`) : t('common.user')}
                    </p>
                    <div className="flex items-center space-x-1 mt-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-xs text-white/70 font-medium">Online</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('BUTTON CLICKED - Starting logout...');
                      handleLogout();
                    }}
                    className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-medium">{t('nav.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export { TopNavbar };
